import type { StoryboardDraft } from './storyboard';

/**
 * Per-(video-ad entity, brand, promotion, template) persistence of a COMPOSED storyboard draft.
 *
 * Two goals: switching promotion/template and back within an editor session must NOT re-call the LLM (the
 * refine is saved and replayed), and the owner's edits to the beats must survive it. localStorage is
 * per-browser and best-effort: any failure (private mode, quota, storage disabled) degrades safely to
 * re-composing next time.
 *
 * Draft-first (Workstream B): the key is scoped to the VIDEO-AD ENTITY, not just (promotion, template).
 * Each "Create new" mints a new entity id, so it never inherits a previous ad's cached compose — it always
 * composes fresh (showing the in-context loader) — and one ad's beats never bleed into another that happens
 * to share the same promotion + template. The entity's authoritative persistence is the backend
 * `storyboard_params` (restored on reopen); this cache is just a fast per-session local mirror.
 */
export interface StoredStoryboard {
  draft: StoryboardDraft;
  /** The owner-selected video model at save time (restored so the dropdown survives a reload too). */
  videoModel?: string;
  savedAt: number;
}

// v2: the default video model changed to Veo (omni-flash 500s on image input), so drop v1 caches whose
// saved model would replay a broken generation — they re-compose with the new default.
// v3: drafts now carry a `promotions[]` array (multi-promo); a v2 cache holds a draft with no promotions, so
// drop it (re-compose from the current draft, which is seeded with promotions).
const KEY_PREFIX = 'ows.storyboard.v3';

/** Storage key for one (entity, brand, promotion, template) tuple. `entityId` scopes the cache to a single
 *  video-ad entity so a fresh "Create new" never replays a different ad's compose (see the module doc); it is
 *  optional for any non-entity/legacy caller, which falls back to the per-(promotion, template) key. */
export const storyboardStorageKey = (
  tenantId: number | string,
  promotionId: string,
  templateId: string,
  entityId?: number | string,
): string =>
  entityId != null
    ? `${KEY_PREFIX}:e${entityId}:${tenantId}:${promotionId}:${templateId}`
    : `${KEY_PREFIX}:${tenantId}:${promotionId}:${templateId}`;

/** Load a saved storyboard, or null when absent/unreadable/malformed. */
export const loadStoredStoryboard = (key: string): StoredStoryboard | null => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredStoryboard;
    if (!parsed?.draft || !Array.isArray(parsed.draft.beats)) return null;
    // A usable draft has v1 beats OR a single-clip shot plan (whose beats are always empty).
    const hasPlan = Array.isArray(parsed.draft.shotPlan?.shots) && (parsed.draft.shotPlan?.shots.length ?? 0) > 0;
    if (parsed.draft.beats.length === 0 && !hasPlan) return null;
    // A cache written before the pipeline existed is a v1 (beats) draft.
    return { ...parsed, draft: { ...parsed.draft, pipeline: parsed.draft.pipeline === 'single_clip' ? 'single_clip' : 'beats' } };
  } catch {
    return null;
  }
};

/** Save a composed/edited storyboard (best-effort; a failure is swallowed). */
export const storeStoryboard = (key: string, draft: StoryboardDraft, videoModel: string): void => {
  try {
    const payload: StoredStoryboard = { draft, videoModel, savedAt: Date.now() };
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    /* best-effort: private mode / quota / disabled — the composer still works, it just re-composes next time */
  }
};

/** Drop a saved storyboard (e.g. "Reset from template" — re-seed + re-compose from scratch). */
export const clearStoredStoryboard = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* best-effort */
  }
};
